import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';

import IconButton from '../ui/icon-button';

import type { Artwork } from './artwork';
import { chipOf, deskAt } from './desk-hit';
import { seatOccupant } from './occupant';
import type { SpaceDef } from './spaces';
import { normaliseDeskId } from './spaces';

export interface FloorMapProps {
  space: SpaceDef;
  artwork: Artwork;
  /** Desk ids already taken, normalised to upper case by the caller. */
  booked: Set<string>;
  /** Controlled selection — the parent owns it so it survives a space swap. */
  selected: string | null;
  onSelect: (deskId: string | null) => void;
  /** Let a booked desk be picked, so an operator can inspect who holds it. */
  allowBookedSelection?: boolean;
  /** Upper floor only: ENTRANCE / KITCHEN / MEETING ROOM markers. */
  showPins?: boolean;
  /** The artwork's own Available / Booked / Selected key. */
  showLegend?: boolean;
  /**
   * Crop the artwork's bottom margin to whatever is actually drawn. That
   * margin was sized around the legend, so a map without one otherwise
   * carries the empty band around inside its frame.
   */
  trimFrame?: boolean;
  /** Desk-number badge size relative to the artwork's own. 1 = as designed. */
  badgeScale?: number;
  /** A little character walks up to the selected desk, sits and opens a laptop. */
  showOccupant?: boolean;
  /** Drawn over the canvas's bottom-right corner — the zoom buttons. */
  controls?: ReactNode;
  initialRotation?: number;
}

/** Breathing room left under the plan when the frame is trimmed to it. */
const FRAME_PAD = 24;

/**
 * One floor's seat map.
 *
 * The artwork is injected once as static markup; desk state is applied by
 * setting data-state on each <g class="desk">, and clicks and hover are
 * resolved on the <svg> root by `deskAt`, from the desks' drawn geometry — so
 * re-rendering never re-parses the artwork, and the desk a click takes is the
 * one that was highlighted.
 *
 * Mount only one instance per page: the artwork carries fixed element ids
 * (#view-0 .. #view-3), and both floors use the same ones.
 */
export default function FloorMap({
  space,
  artwork,
  booked,
  selected,
  onSelect,
  allowBookedSelection = false,
  showPins = true,
  showLegend = true,
  trimFrame = false,
  badgeScale = 0.76,
  showOccupant = true,
  initialRotation = 0,
  controls,
}: FloorMapProps) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [rot, setRot] = useState(((initialRotation % 4) + 4) % 4);
  /** What the character was last seated for, so turning the view does not replay the walk. */
  const seated = useRef<{ desk: string | null; rot: number }>({ desk: null, rot });

  // Paint desk state onto the injected artwork.
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;

    el.querySelectorAll<SVGGElement>('.desk').forEach((g) => {
      const id = g.getAttribute('data-desk-id') ?? '';
      // A copy laid over a seated character keeps the state it was copied with.
      if (!id) return;
      const isBooked = booked.has(id);
      const isSelected = selected === id;
      g.setAttribute('data-state', isBooked ? 'booked' : isSelected ? 'selected' : 'available');
      // A booked desk can still be the active one when the caller allows it to
      // be inspected, so the ring is tracked separately from the fill state.
      if (isSelected && isBooked) g.setAttribute('data-active', 'true');
      else g.removeAttribute('data-active');
      g.setAttribute('data-selectable', String(!isBooked || allowBookedSelection));
      // Someone is sat at a booked desk, and they say so better than its number does.
      g.querySelectorAll<SVGGElement>(':scope > .badge').forEach((badge) => {
        badge.style.display = isBooked && showOccupant ? 'none' : '';
      });

      const title = g.querySelector('title');
      if (title) {
        title.textContent = `${id}: ${isBooked ? 'booked' : isSelected ? 'selected' : 'available'}`;
      }
    });

    el.querySelectorAll<SVGGElement>('.pins').forEach((g) => {
      g.style.display = showPins ? '' : 'none';
    });
    // The key sits outside the rotation views, so it is addressed on its own.
    el.querySelectorAll<SVGGElement>('#legend').forEach((g) => {
      g.style.display = showLegend ? '' : 'none';
    });
    for (let i = 0; i < 4; i += 1) {
      const view = el.querySelector<SVGGElement>(`#view-${i}`);
      if (view) view.style.display = i === rot ? '' : 'none';
    }
  }, [artwork, booked, selected, allowBookedSelection, showPins, showLegend, showOccupant, rot]);

  // The artwork draws desk numbers big enough to cover most of the desktop
  // (r=12 on a desk ~40 units wide). Shrink each badge about its own centre,
  // taken from its .badge-face chip so it stays exactly where the design put
  // it. Done here, not in the artwork, because re-exporting the design
  // overwrites those files.
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    el.querySelectorAll<SVGGElement>('.badge').forEach((badge) => {
      const face = badge.querySelector('.badge-face');
      const chip = face ? chipOf(face) : null;
      if (!chip) return;
      const { cx, cy } = chip;
      if (badgeScale === 1) {
        badge.removeAttribute('transform');
        return;
      }
      badge.setAttribute(
        'transform',
        `translate(${cx} ${cy}) scale(${badgeScale}) translate(${-cx} ${-cy})`,
      );
    });
  }, [badgeScale, artwork]);

  // Runs after the two effects above, so what it measures is what will be
  // painted: anything they hid is already out of the artwork's bounds.
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const [x, y, w, h] = artwork.viewBox.split(/[ ,]+/).map(Number);
    if (![x, y, w, h].every(Number.isFinite)) return;
    // The frame's proportions, for a canvas that sizes the artwork to fit a
    // box of its own (`.seatmap--fill`) rather than the other way round.
    const setAspect = (height: number) =>
      el.parentElement?.style.setProperty('--map-aspect', String(w / height));
    if (!trimFrame) {
      el.setAttribute('viewBox', artwork.viewBox);
      setAspect(h);
      return;
    }
    // Measured across all four rotations, so turning the view never resizes
    // the frame. Each is shown for the measurement only — no paint happens in
    // between, so none of this reaches the screen.
    let bottom = -Infinity;
    for (let i = 0; i < 4; i += 1) {
      const view = el.querySelector<SVGGElement>(`#view-${i}`);
      if (!view) continue;
      const shown = view.style.display;
      view.style.display = '';
      const box = view.getBBox();
      view.style.display = shown;
      if (box.height) bottom = Math.max(bottom, box.y + box.height);
    }
    if (!Number.isFinite(bottom)) {
      setAspect(h);
      return;
    }
    const trimmed = Math.min(h, Math.ceil(bottom - y) + FRAME_PAD);
    el.setAttribute('viewBox', `${x} ${y} ${w} ${trimmed}`);
    setAspect(trimmed);
  }, [trimFrame, artwork, showPins, showLegend, badgeScale, rot]);

  // After the frame trim above, so its measurement never includes the character: React runs
  // every cleanup before any effect, so on a rotation they are already gone when it measures.
  const selectedIsBooked = selected !== null && booked.has(selected);
  useEffect(() => {
    const el = svgRef.current;
    const view = el?.querySelector(`#view-${rot}`);
    if (!view || !showOccupant || !selected || selectedIsBooked) return;
    // Only a turn of the view redraws them seated; a new pick, or StrictMode's second run of the
    // same one, walks them in.
    const turned = seated.current.desk === selected && seated.current.rot !== rot;
    seated.current = { desk: selected, rot };
    return seatOccupant(view, selected, !turned) ?? undefined;
  }, [artwork, selected, selectedIsBooked, rot, showOccupant]);

  // Every booked desk has someone at it already, sat and working. Seated back to front, so a
  // nearer one paints over a farther one. A booked desk picked for inspection keeps its sitter
  // rather than walking a second one in.
  useEffect(() => {
    const view = svgRef.current?.querySelector(`#view-${rot}`);
    if (!view || !showOccupant || booked.size === 0) return;
    const nearness = (id: string) =>
      view.querySelector(`.desk[data-desk-id="${id}"] .top`)?.getBoundingClientRect().bottom ?? 0;
    const leave = [...booked]
      .sort((a, b) => nearness(a) - nearness(b))
      .map((id) => seatOccupant(view, id, false));
    return () => leave.forEach((go) => go?.());
  }, [artwork, booked, rot, showOccupant]);

  const handleMapClick = useCallback(
    (event: MouseEvent<SVGSVGElement>) => {
      const id = normaliseDeskId(deskAt(event.currentTarget, event.clientX, event.clientY) ?? '');
      if (!id) return;
      if (booked.has(id) && !allowBookedSelection) return;
      // Called directly rather than from inside a state updater: updaters are
      // double-invoked under StrictMode, which would fire this twice per click.
      onSelect(selected === id ? null : id);
    },
    [booked, allowBookedSelection, onSelect, selected],
  );

  /** The desk the pointer is over, highlighted and given the cursor a click on it will honour. */
  const hovered = useRef<string | null>(null);
  const markHover = useCallback(
    (svg: SVGSVGElement, id: string | null) => {
      if (id !== hovered.current) {
        svg.querySelectorAll('.desk[data-hover]').forEach((g) => g.removeAttribute('data-hover'));
        if (id) {
          svg
            .querySelectorAll(`.desk[data-desk-id="${id}"]`)
            .forEach((g) => g.setAttribute('data-hover', 'true'));
        }
        hovered.current = id;
      }
      const blocked = id !== null && booked.has(normaliseDeskId(id)) && !allowBookedSelection;
      if (id) svg.setAttribute('data-pointer', blocked ? 'blocked' : 'desk');
      else svg.removeAttribute('data-pointer');
    },
    [booked, allowBookedSelection],
  );
  const handlePointerMove = (event: PointerEvent<SVGSVGElement>) =>
    markHover(event.currentTarget, deskAt(event.currentTarget, event.clientX, event.clientY));
  const handlePointerLeave = (event: PointerEvent<SVGSVGElement>) =>
    markHover(event.currentTarget, null);

  const rotate = (delta: number) => setRot((r) => (r + delta + 4) % 4);

  /**
   * Held stable across renders. React compares the dangerouslySetInnerHTML
   * object by reference, so a fresh literal re-injects the artwork on every
   * render — which re-parses it and, worse, throws away the desk state the
   * effects above painted onto it.
   */
  const markup = useMemo(() => ({ __html: artwork.svg }), [artwork.svg]);

  return (
    <div className="seatmap__canvas">
      <span className="seatmap__angle">{rot * 90}°</span>
      <svg
        ref={svgRef}
        onClick={handleMapClick}
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerLeave}
        viewBox={artwork.viewBox}
        role="group"
        aria-label={`Isometric seat map, ${space.name}`}
        className="seatmap__svg"
        dangerouslySetInnerHTML={markup}
      />
      {/* Over the map's own edges, so turning the view reads as turning the floor. */}
      <IconButton
        label="Rotate view left"
        size="compact"
        className="seatmap__rotbtn seatmap__rotbtn--left"
        onClick={() => rotate(-1)}
      >
        <ChevronLeftIcon aria-hidden="true" className="size-5" />
      </IconButton>
      <IconButton
        label="Rotate view right"
        size="compact"
        className="seatmap__rotbtn seatmap__rotbtn--right"
        onClick={() => rotate(1)}
      >
        <ChevronRightIcon aria-hidden="true" className="size-5" />
      </IconButton>
      {controls && <div className="seatmap__controls">{controls}</div>}
    </div>
  );
}
